import '../../assets/styles/CameraFont.module.css';

import {
    deleteAnomaly,
    openDoor,
    useAiVideoUrl,
    useAnomalies,
    useBoutiques,
    useCheckinKiosks,
    useCheckoutKiosks,
    useLiveAiArticles,
    useLiveAnomalies,
    usePreAuthorizations,
    useStoreOpeningState,
    useVideoIds,
} from '@gozoki/api';
import { Kiosk as KioskType, UniqueArticle } from '@gozoki/api-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { useMutation } from '@tanstack/react-query';
import InterventionButtons from '../../components/buttons/Intervention.tsx';
import { useToast } from '../../components/communication/Toast';
import Page from '../../components/Page';
import { StoresPicker } from '../../utils/dashboard/storesPicker';
import { usePreAuthorizationNotifications } from '../../utils/websockets/usePreAuthorizationNotifications';
import AnomalyCards from './AnomalyCards';
import CameraWindow from './CameraWindow';
import PreAuthorizationCards from './PreAuthorizationCards';

const CamerasPages = () => {
    const [searchParams] = useSearchParams();

    const storeId = useMemo(() => {
        if (searchParams.get('storeId')) {
            return searchParams.get('storeId') || '3';
        }
        return '3';
    }, [searchParams]);
    const [centralVideoId, setCentralVideoId] = useState<string | null>(null);

    const [Toast, showToast] = useToast();

    const { data: boutiques } = useBoutiques();
    const { data: videoIds } = useVideoIds(storeId);
    const { data: checkinKiosks } = useCheckinKiosks(Number(storeId));

    const { data: checkoutKiosks } = useCheckoutKiosks(Number(storeId));

    const { data: storeOpeningState } = useStoreOpeningState(Number(storeId));

    const isAttendedMode = storeOpeningState?.openingMode === 'ATTENDED';

    const checkinKioskId = checkinKiosks?.length ? checkinKiosks[0].id : null;

    const { data: anomalies } = useAnomalies(storeId);
    useLiveAnomalies(storeId);

    const { data: preAuthorizations, refetch: refetchPreAuthorizations } = usePreAuthorizations(
        Number(storeId)
    );

    const handleNewPreAuthorization = useCallback(
        (data: { storeId?: number }) => {
            if (data.storeId === Number(storeId)) {
                refetchPreAuthorizations();
            }
        },
        [storeId, refetchPreAuthorizations]
    );

    usePreAuthorizationNotifications(handleNewPreAuthorization);

    const navigate = useNavigate();

    useEffect(() => {
        navigate(`/cameras?storeId=${storeId}`);
    }, [storeId, navigate]);

    useLiveAnomalies(storeId);
    useLiveAiArticles(storeId);

    const { data: mainVideoUrl } = useAiVideoUrl(storeId, centralVideoId);

    const { mutate: mutateDeleteAnomaly } = useMutation({
        mutationFn: ({ sId, aId }: { sId: string; aId: string }) => deleteAnomaly(sId, aId),
        onError: () => {
            showToast({
                severity: 'error',
                message: "Une erreur est survenue lors de la suppression de l'anomalie",
            });
        },
    });

    const handleDeleteAnomaly = (anomalyId: string) => {
        mutateDeleteAnomaly({ sId: storeId, aId: anomalyId });
    };

    const handleVideoClick = (videoId: string) => {
        if (videoId !== centralVideoId) {
            setCentralVideoId(videoId);
        }
    };

    useEffect(() => {
        if (videoIds && videoIds.length > 0) {
            setCentralVideoId(videoIds[0]);
        }
    }, [videoIds]);

    const handleChangeStore = useCallback(
        (sId: number) => {
            navigate(`/cameras?storeId=${sId}`);
        },
        [navigate]
    );

    return (
        <Page>
            <div style={{ marginLeft: '25px' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: '20px',
                        // marginTop: '10px',
                        height: '100%',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            gap: '10px',
                            height: '100%',
                            // flex: 3,
                        }}
                    >
                        <div
                            style={{
                                color: 'darkgreen',
                                fontFamily: 'BlockBerthold_regular',
                                fontWeight: '400',
                                fontSize: '40px',
                                // display: 'flex',
                            }}
                        >
                            Caméras
                        </div>
                        <div style={{ display: 'flex', gap: '16px' }}>
                            <div style={{ flex: 1 }}>
                                <InterventionButtons
                                    checkType="CHECK-IN"
                                    kioskId={checkinKioskId}
                                    storeId={Number(storeId)}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                {checkinKioskId && (
                                    <div style={{ marginTop: '55px', height: '92px' }}>
                                        <Button
                                            variant="contained"
                                            sx={{
                                                'backgroundColor': 'orange',
                                                '&:hover': { backgroundColor: 'darkorange' },
                                                'width': '100%',
                                                'height': '100%',
                                            }}
                                            onClick={() => openDoor(checkinKioskId, true)}
                                            disabled={!isAttendedMode}
                                        >
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="center"
                                                justifyContent="center"
                                                gap="8px"
                                                style={{
                                                    color: '#3E2500',
                                                    font: 'Avenir Next',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <LockOpenIcon
                                                        fontSize="large"
                                                        style={{ width: 24, height: 24 }}
                                                    />
                                                    Ouvrir la porte
                                                </span>
                                            </Box>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div
                            style={{
                                position: 'relative',
                                maxHeight: '80vh',
                                maxWidth: '80vh',
                            }}
                        >
                            <div style={{ maxHeight: '80vh', maxWidth: '80vh', aspectRatio: 1.52 }}>
                                <img
                                    alt={`camera ${centralVideoId}`}
                                    src={mainVideoUrl}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: '8px solid #FF9900',
                                        borderRadius: '16px',
                                        aspectRatio: 1.5,
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '21px',
                                    // maxHeight: '80vh',
                                    // maxWidth: '100vh',
                                    overflowX: 'auto',
                                }}
                            >
                                {videoIds?.map((videoId) => (
                                    <CameraWindow
                                        key={videoId}
                                        videoId={videoId}
                                        storeId={storeId}
                                        onClick={() => handleVideoClick(videoId)}
                                        isSelected={centralVideoId === videoId}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            gap: '20px',
                        }}
                    >
                        <Toast />
                        <Box
                            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                            py={2}
                        >
                            <Box sx={{ width: 500 }}>
                                <StoresPicker
                                    onlyBoutiques
                                    defaultStore={Number.parseInt(storeId, 10)}
                                    setUniqueStore={handleChangeStore}
                                    usedStores={boutiques?.map((s) => s.id)}
                                />
                            </Box>
                        </Box>
                        {checkoutKiosks?.length ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {checkoutKiosks.map((kiosk) => (
                                    <KioskPanel
                                        key={kiosk.id}
                                        kiosk={kiosk}
                                        storeId={Number(storeId)}
                                    />
                                ))}
                            </div>
                        ) : null}

                        <div style={{ height: '80%', overflowY: 'auto' }}>
                            <AnomalyCards anomalies={anomalies} onDelete={handleDeleteAnomaly} />
                        </div>
                        {/* {storeOpeningState?.openingMode === 'UNATTENDED_PRE_AUTH' && ( */}
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <PreAuthorizationCards
                                preAuthorizations={preAuthorizations ?? []}
                                storeId={Number(storeId)}
                                showToast={showToast}
                            />
                        </div>
                        {/* )} */}
                        <div style={{ flexGrow: 1 }} />
                    </div>
                </div>
            </div>
        </Page>
    );
};

interface KioskPanelProps {
    kiosk: KioskType;
    storeId: number;
}

const KioskPanel = ({ kiosk, storeId }: KioskPanelProps) => {
    const { cart } = kiosk;

    return (
        <div
            style={{
                border: '1px solid #D9D9D9',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
            }}
        >
            <div
                style={{
                    fontWeight: 600,
                    fontSize: '18px',
                    fontFamily: 'Avenir Next',
                }}
            >
                {kiosk.label}
            </div>

            <InterventionButtons checkType="" kioskId={kiosk.id} storeId={storeId} />

            {cart && cart.articles && cart.articles.length > 0 && (
                <div>
                    <div
                        style={{
                            fontFamily: 'Avenir Next',
                            fontWeight: 600,
                            fontSize: '16px',
                            color: '#2E7D32',
                            marginBottom: '8px',
                        }}
                    >
                        Panier en cours ({cart.articles.length} articles)
                    </div>
                    <ul
                        style={{
                            maxHeight: '150px',
                            overflowY: 'auto',
                            paddingLeft: '16px',
                            margin: 0,
                            fontFamily: 'Avenir Next',
                            fontSize: '14px',
                            lineHeight: '1.4',
                        }}
                    >
                        {cart.articles.map((article: UniqueArticle) => (
                            <li
                                key={article.id}
                                style={{
                                    marginBottom: '4px',
                                    color: '#333',
                                }}
                            >
                                {article.product?.label ?? 'Article'}&nbsp;&nbsp;(
                                {article.product.price}€)
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default CamerasPages;
